import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import productService, { Product } from '../services/productService';
import ProductForm from '../components/products/ProductForm';
import ReceiveStockModal from '../components/products/ReceiveStockModal';
import StockMovementDetailModal from '../components/stock/StockMovementDetailModal';

type TabType = 'general' | 'inventory' | 'movements' | 'serials' | 'accounting' | 'history';

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  textAlign: 'left',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  background: '#ffffff',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(239,232,213,0.5)',
  verticalAlign: 'middle',
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const ghostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#5b5a52',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const dangerButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#c0504a',
  border: '1px solid rgba(192,80,74,0.3)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(null);

  const { data: productData, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProduct(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => productService.deleteProduct(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate('/products');
    },
  });

  const product = productData?.data as Product;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f4efe2' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ padding: '32px 24px' }}>
        <div style={{ background: 'rgba(192,80,74,0.08)', border: '1px solid rgba(192,80,74,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 400, color: '#c0504a', marginBottom: 8 }}>Product Not Found</div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c', marginBottom: 16 }}>The product you're looking for doesn't exist.</p>
          <Link to="/products" style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#d97706', textDecoration: 'none', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      deleteMutation.mutate();
    }
  };

  const tabs: { id: TabType; label: string; disabled?: boolean }[] = [
    { id: 'general', label: 'General' },
    { id: 'inventory', label: 'Inventory', disabled: product.itemType === 'SERVICE' },
    { id: 'movements', label: 'Stock Movements', disabled: product.itemType === 'SERVICE' },
    { id: 'serials', label: 'Serial Numbers', disabled: !product.isSerialized },
    { id: 'accounting', label: 'Accounting' },
    { id: 'history', label: 'History' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Back Link */}
      <div>
        <Link
          to="/products"
          style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', textDecoration: 'none', letterSpacing: '0.2em', textTransform: 'uppercase' }}
        >
          Back to Products
        </Link>
      </div>

      {/* Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
              Products
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
              {product.name}
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
              SKU: {product.sku}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {product.itemType !== 'SERVICE' && (
              <button onClick={() => setShowReceiveModal(true)} style={primaryButtonStyle}>
                <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Receive Stock
              </button>
            )}
            <button onClick={() => setShowEditModal(true)} style={ghostButtonStyle}>
              Edit Product
            </button>
            <button onClick={handleDelete} style={dangerButtonStyle}>
              Delete
            </button>
          </div>
        </div>

        {/* Status Badges */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <span style={{
            padding: '3px 10px',
            borderRadius: 3,
            fontFamily: "'Inter', sans-serif",
            fontSize: 9,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            background: product.isActive ? 'rgba(90,180,130,0.1)' : 'rgba(239,232,213,0.6)',
            color: product.isActive ? '#5ab482' : '#8a8778',
            border: product.isActive ? '1px solid rgba(90,180,130,0.2)' : '1px solid rgba(92,78,51,0.2)',
          }}>
            {product.isActive ? 'Active' : 'Inactive'}
          </span>
          <span style={{ padding: '3px 10px', borderRadius: 3, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', background: 'rgba(92,78,51,0.2)', color: '#d97706', border: '1px solid rgba(92,78,51,0.3)' }}>
            {product.itemType}
          </span>
          {product.isSerialized && (
            <span style={{ padding: '3px 10px', borderRadius: 3, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', background: 'rgba(150,100,200,0.1)', color: '#a064c8', border: '1px solid rgba(150,100,200,0.2)' }}>
              Serialized
            </span>
          )}
          {product.isLowStock && (
            <span style={{ padding: '3px 10px', borderRadius: 3, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', background: 'rgba(192,80,74,0.1)', color: '#c0504a', border: '1px solid rgba(192,80,74,0.2)' }}>
              Low Stock
            </span>
          )}
        </div>
      </div>

      {/* Tabs + Content */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
        {/* Tab Nav */}
        <div style={{ borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', overflowX: 'auto' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              style={{
                padding: '14px 20px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 400,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #d97706' : '2px solid transparent',
                background: 'transparent',
                color: tab.disabled ? '#c9c2ab' : activeTab === tab.id ? '#d97706' : '#8a8778',
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {activeTab === 'general' && <GeneralTab product={product} />}
          {activeTab === 'inventory' && <InventoryTab product={product} />}
          {activeTab === 'movements' && <MovementsTab product={product} onViewMovement={setSelectedMovementId} />}
          {activeTab === 'serials' && <SerialsTab product={product} />}
          {activeTab === 'accounting' && <AccountingTab product={product} />}
          {activeTab === 'history' && <HistoryTab product={product} />}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <ProductForm
          productId={product.id}
          initialData={product}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries({ queryKey: ['product', id] });
          }}
        />
      )}

      {showReceiveModal && (
        <ReceiveStockModal
          isOpen={showReceiveModal}
          onClose={() => setShowReceiveModal(false)}
          onSuccess={() => {
            setShowReceiveModal(false);
            queryClient.invalidateQueries({ queryKey: ['product', id] });
          }}
          product={{
            id: product.id,
            name: product.name,
            sku: product.sku,
            isSerialized: product.isSerialized,
            costPrice: product.costPrice,
          }}
        />
      )}

      {selectedMovementId && (
        <StockMovementDetailModal
          isOpen={!!selectedMovementId}
          onClose={() => setSelectedMovementId(null)}
          movementId={selectedMovementId}
        />
      )}
    </div>
  );
};

// General Tab Component
const GeneralTab: React.FC<{ product: Product }> = ({ product }) => {
  const sectionHeading: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 18,
    fontWeight: 400,
    color: '#1c2033',
    marginBottom: 16,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6a5c',
    display: 'block',
    marginBottom: 4,
  };

  const valueStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    color: '#1c2033',
    margin: 0,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
      <div>
        <div style={sectionHeading}>Basic Information</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { label: 'SKU', value: product.sku },
            { label: 'Name', value: product.name },
            { label: 'Description', value: product.description || 'N/A' },
            { label: 'Category', value: product.category?.name || 'Uncategorized' },
            { label: 'Item Type', value: product.itemType },
          ].map((field) => (
            <div key={field.label}>
              <label style={labelStyle}>{field.label}</label>
              <p style={valueStyle}>{field.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={sectionHeading}>Pricing</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Unit Price</label>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#d97706', margin: 0 }}>
              ${product.unitPrice.toFixed(2)}
            </p>
          </div>
          <div>
            <label style={labelStyle}>Cost Price</label>
            <p style={valueStyle}>${product.costPrice.toFixed(2)}</p>
          </div>
          <div>
            <label style={labelStyle}>Profit Margin</label>
            <p style={{ ...valueStyle, color: product.unitPrice > product.costPrice ? '#5ab482' : '#c0504a', fontWeight: 500 }}>
              {product.unitPrice > 0
                ? (((product.unitPrice - product.costPrice) / product.unitPrice) * 100).toFixed(2)
                : 0}%
            </p>
          </div>
          <div>
            <label style={labelStyle}>Tax Code</label>
            <p style={valueStyle}>
              {product.taxCode ? `${product.taxCode.name} (${product.taxCode.rate}%)` : 'No Tax'}
            </p>
          </div>
        </div>
      </div>

      {product.itemType !== 'SERVICE' && (
        <div>
          <div style={sectionHeading}>Inventory Settings</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Track Inventory', value: product.trackInventory ? 'Yes' : 'No' },
              { label: 'Reorder Point', value: product.reorderPoint || 'Not set' },
              { label: 'Reorder Quantity', value: product.reorderQty || 'Not set' },
              { label: 'Preferred Vendor', value: product.preferredVendor?.name || 'Not set' },
              { label: 'Lead Time', value: product.leadTimeDays ? `${product.leadTimeDays} days` : 'Not set' },
            ].map((field) => (
              <div key={field.label}>
                <label style={labelStyle}>{field.label}</label>
                <p style={valueStyle}>{field.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={sectionHeading}>Additional Details</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { label: 'Barcode', value: product.barcode || 'N/A' },
            { label: 'Weight', value: product.weight ? `${product.weight} kg` : 'N/A' },
            { label: 'Dimensions', value: product.dimensions || 'N/A' },
            { label: 'Created', value: new Date(product.createdAt).toLocaleDateString() },
            { label: 'Last Updated', value: new Date(product.updatedAt).toLocaleDateString() },
          ].map((field) => (
            <div key={field.label}>
              <label style={labelStyle}>{field.label}</label>
              <p style={valueStyle}>{field.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Inventory Tab Component
const InventoryTab: React.FC<{ product: Product }> = ({ product }) => {
  const rentedCount = product.rentedCount || 0;
  const shouldShowRented = product.isSerialized && rentedCount > 0;

  const statCards = [
    { label: 'Total Stock', value: product.totalStock || 0, color: '#d97706' },
    { label: 'Available', value: product.availableStock || 0, color: '#5ab482' },
    { label: 'Reserved', value: product.totalReserved || 0, color: '#c0a040' },
    ...(shouldShowRented ? [{ label: 'Rented', value: rentedCount, color: '#a064c8' }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${statCards.length}, 1fr)`, gap: 16, marginBottom: 32 }}>
        {statCards.map((stat) => (
          <div key={stat.label} style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '20px 24px' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 10 }}>{stat.label}</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033', marginBottom: 16 }}>
        Warehouse Stock Levels
      </div>
      {product.warehouseStock && product.warehouseStock.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Warehouse', 'Code', 'On Hand', 'Reserved', 'Available', 'Bin Location'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {product.warehouseStock.map((stock, index) => (
                <tr key={index}>
                  <td style={{ ...tdStyle, color: '#1c2033', fontWeight: 500 }}>{stock.warehouse.name}</td>
                  <td style={tdStyle}>{stock.warehouse.code}</td>
                  <td style={tdStyle}>{stock.quantityOnHand}</td>
                  <td style={tdStyle}>{stock.quantityReserved}</td>
                  <td style={{ ...tdStyle, color: '#5ab482', fontWeight: 500 }}>{stock.quantityOnHand - stock.quantityReserved}</td>
                  <td style={{ ...tdStyle, color: '#6b6a5c' }}>{stock.binLocation || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 24px', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>
          No warehouse stock data available
        </div>
      )}
    </div>
  );
};

// Stock Movements Tab Component
const MovementsTab: React.FC<{ product: Product; onViewMovement: (id: string) => void }> = ({ product, onViewMovement }) => {
  const navigate = useNavigate();

  const handleMovementClick = (movement: any) => {
    if (movement.movementType === 'RENTAL' && movement.rentalContract) {
      navigate(`/rentals/${movement.rentalContract.id}`);
      return;
    }
    if (movement.movementType === 'OUT' && movement.invoice) {
      navigate(`/invoices/${movement.invoice.id}`);
      return;
    }
    if (movement.referenceType && ['MANUAL', 'TRANSFER', 'BILL', 'INVOICE'].includes(movement.referenceType)) {
      onViewMovement(movement.id);
    }
  };

  const isClickable = (movement: any) =>
    (movement.movementType === 'RENTAL' && movement.rentalContract) ||
    (movement.movementType === 'OUT' && movement.invoice) ||
    (movement.referenceType && ['INVOICE', 'BILL', 'MANUAL', 'TRANSFER'].includes(movement.referenceType));

  const getMovementTypeBadge = (type: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 3,
      fontFamily: "'Inter', sans-serif",
      fontSize: 9,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    };
    if (type === 'IN') return { ...base, background: 'rgba(90,180,130,0.1)', color: '#5ab482', border: '1px solid rgba(90,180,130,0.2)' };
    if (type === 'OUT') return { ...base, background: 'rgba(192,80,74,0.1)', color: '#c0504a', border: '1px solid rgba(192,80,74,0.2)' };
    if (type === 'RENTAL') return { ...base, background: 'rgba(150,100,200,0.1)', color: '#a064c8', border: '1px solid rgba(150,100,200,0.2)' };
    return { ...base, background: 'rgba(92,78,51,0.2)', color: '#d97706', border: '1px solid rgba(92,78,51,0.3)' };
  };

  return (
    <div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033', marginBottom: 16 }}>
        Recent Stock Movements &amp; Rentals
      </div>
      {product.stockMovements && product.stockMovements.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Type', 'Details', 'Warehouse', 'Quantity'].map((h, i) => (
                  <th key={h} style={{ ...thStyle, textAlign: i === 4 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {product.stockMovements.map((movement: any) => (
                <tr
                  key={movement.id}
                  onClick={() => handleMovementClick(movement)}
                  style={{ cursor: isClickable(movement) ? 'pointer' : 'default', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => { if (isClickable(movement)) e.currentTarget.style.background = 'rgba(248,250,252,1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={tdStyle}>{new Date(movement.date).toLocaleDateString()}</td>
                  <td style={tdStyle}><span style={getMovementTypeBadge(movement.movementType)}>{movement.movementType}</span></td>
                  <td style={tdStyle}>
                    {movement.movementType === 'RENTAL' && movement.rentalContract ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', fontWeight: 500 }}>{movement.rentalContract.contractNumber}</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>{movement.rentalContract.customer.name}</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#8a8778', letterSpacing: '0.1em' }}>SN: {movement.serialNumber}</span>
                        {movement.status === 'RETURNED' && movement.returnDate && (
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ab482' }}>Returned: {new Date(movement.returnDate).toLocaleDateString()}</span>
                        )}
                        {movement.status === 'RENTED' && (
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#a064c8' }}>Currently Rented</span>
                        )}
                      </div>
                    ) : movement.movementType === 'OUT' && movement.invoice ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', fontWeight: 500 }}>{movement.invoice.invoiceNumber}</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>{movement.invoice.customer.name}</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#8a8778', letterSpacing: '0.1em' }}>SN: {movement.serialNumber}</span>
                      </div>
                    ) : movement.referenceNumber ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: ['INVOICE', 'BILL', 'MANUAL', 'TRANSFER'].includes(movement.referenceType) ? '#d97706' : '#1c2033', fontWeight: 500 }}>
                          {movement.referenceNumber}
                        </span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                          {movement.referenceType === 'INVOICE' && 'Invoice'}
                          {movement.referenceType === 'BILL' && 'Bill'}
                          {movement.referenceType === 'ADJUSTMENT' && 'Stock Adjustment'}
                          {movement.referenceType === 'TRANSFER' && 'Warehouse Transfer'}
                          {movement.referenceType === 'MANUAL' && 'Manual Entry'}
                        </span>
                        {movement.notes && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778' }}>{movement.notes}</span>}
                      </div>
                    ) : (
                      <span style={{ color: '#8a8778' }}>-</span>
                    )}
                  </td>
                  <td style={tdStyle}>{movement.warehouse?.name || 'N/A'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#1c2033', fontWeight: 500 }}>{movement.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 24px', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>
          No stock movements recorded yet
        </div>
      )}
    </div>
  );
};

// Serial Numbers Tab Component
const SerialsTab: React.FC<{ product: Product }> = ({ product }) => {
  const getSerialStatusStyle = (status: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 3,
      fontFamily: "'Inter', sans-serif",
      fontSize: 9,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    };
    if (status === 'IN_STOCK') return { ...base, background: 'rgba(90,180,130,0.1)', color: '#5ab482', border: '1px solid rgba(90,180,130,0.2)' };
    if (status === 'SOLD') return { ...base, background: 'rgba(92,78,51,0.2)', color: '#d97706', border: '1px solid rgba(92,78,51,0.3)' };
    return { ...base, background: 'rgba(239,232,213,0.6)', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.2)' };
  };

  return (
    <div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033', marginBottom: 16 }}>
        Serial Numbers
      </div>
      {product.serialNumbers && product.serialNumbers.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Serial Number', 'Status', 'Warehouse'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {product.serialNumbers.map((serial) => (
                <tr key={serial.id}>
                  <td style={{ ...tdStyle, color: '#1c2033', fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>{serial.serialNumber}</td>
                  <td style={tdStyle}><span style={getSerialStatusStyle(serial.status)}>{serial.status}</span></td>
                  <td style={tdStyle}>{serial.warehouse ? `${serial.warehouse.name} (${serial.warehouse.code})` : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 24px', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>
          No serial numbers registered yet
        </div>
      )}
    </div>
  );
};

// Accounting Tab Component
const AccountingTab: React.FC<{ product: Product }> = ({ product }) => {
  const labelStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6a5c',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033', marginBottom: 16 }}>
          Accounting Configuration
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            { label: 'Income Account', value: product.incomeAccount ? `${product.incomeAccount.code} - ${product.incomeAccount.name}` : 'Not set (uses category / company default)' },
            { label: 'COGS Account', value: product.cogsAccount ? `${product.cogsAccount.code} - ${product.cogsAccount.name}` : 'Not set (uses category / company default)' },
            { label: 'Asset Account', value: product.assetAccount ? `${product.assetAccount.code} - ${product.assetAccount.name}` : 'Not set (uses category / company default)' },
            { label: 'Tax Code', value: product.taxCode ? `${product.taxCode.name} (${product.taxCode.rate}%)` : 'No Tax' },
          ].map((field) => (
            <div key={field.label}>
              <label style={labelStyle}>{field.label}</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0, fontWeight: 500 }}>{field.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'rgba(239,232,213,0.5)', border: '1px solid rgba(92,78,51,0.22)', borderRadius: 4, padding: 16 }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
          <strong style={{ color: '#d97706' }}>Note:</strong> These accounts are used for automatic journal entries when this product is purchased or sold.
        </p>
      </div>
    </div>
  );
};

// History Tab Component
const HistoryTab: React.FC<{ product: Product }> = ({ product }) => {
  const labelStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6a5c',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <label style={labelStyle}>Created At</label>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0, fontWeight: 500 }}>
              {new Date(product.createdAt).toLocaleString()}
            </p>
          </div>
          <div>
            <label style={labelStyle}>Last Updated</label>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0, fontWeight: 500 }}>
              {new Date(product.updatedAt).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'center', padding: '32px 24px', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#8a8778' }}>
        Full audit history coming soon...
      </div>
    </div>
  );
};

export default ProductDetailPage;
